import { DURATION_MILESTONES } from "./economy";
import { getPowerupMarks, getPowerupMultiplier, PowerupSession } from "./powerups";

/**
 * Curses: spites with teeth. A visual spite only gets in the way; a curse
 * also changes how its victim earns, gambles or dresses while it lasts.
 * They're cast, blocked, bounced, scrubbed and booby-trapped exactly like
 * any other spite (a curse is a spite whose shop item sets
 * `options.curse`), so every spite defence works on them too.
 *
 * Instant pranks (shop type "curse", see CURSE_ITEM) do their damage the
 * moment they're bought instead, and are handled by
 * functions/src/actions/curses.ts.
 *
 * Pure and environment-agnostic, so the backend and the live session card
 * work out earnings the same way.
 *
 * Time is sacred: no curse or prank ever changes how long a session lasts
 * or how much pub time it logs; they only touch coin, luck and looks.
 * Nothing checks anyone out, and jail still logs your time.
 *
 * Economy guard rails:
 * - Jail, Flat Pint and the Black Cat only ever stop coin being made, and
 *   cost coin to cast, so they can only shrink the coin supply.
 * - The Leech and the Loan Shark move coin between players rather than
 *   minting it, and each one's take is capped (LEECH.cap, LOAN_SHARK.cap)
 *   below what it costs for long enough to reach the cap, so they're
 *   pranks, not investments. economy-sim.test.ts checks this.
 * - Instant pranks are transfers (Your Round, the Cat Burglar's loot and
 *   fines) or burns, never mints.
 */

export type CurseKind =
  | "jail"
  | "jinx"
  | "flatPint"
  | "leech"
  | "loanShark"
  | "wardrobe"
  | "clown"
  | "kickMe";

/** Shop item ids of the timed curses (spites with `options.curse`). */
export const CURSE_SPITE = {
  jail: "jail",
  jinx: "black-cat",
  flatPint: "flat-pint",
  leech: "leech",
  loanShark: "loan-shark",
  wardrobe: "wardrobe-malfunction",
  clown: "clown-wig",
  kickMe: "kick-me",
} as const satisfies Record<CurseKind, string>;

/** Shop item ids of the instant pranks (shop type "curse"). */
export const CURSE_ITEM = {
  swap: "freaky-friday",
  burglar: "cat-burglar",
  round: "your-round",
  raid: "police-raid",
  snitch: "snitch",
} as const;

/** How much of a pub's hourly coin you keep with a Flat Pint. */
export const FLAT_PINT_RATE = 0.5;

/** The share of their hourly pub coin a Leech drains, and the most one Leech can take. */
export const LEECH = { share: 0.5, cap: 100 } as const;

/** The share of each gambling win a Loan Shark takes, and the most per win. */
export const LOAN_SHARK = { share: 0.25, cap: 100 } as const;

/** The chance a win still pays out under a Black Cat, so it halves your odds. */
export const BLACK_CAT_LUCK = 0.5;

/** What a spite costs to cast at someone wearing a Kick Me sign. */
export const KICK_ME_DISCOUNT = 0.5;

/** The hat a Clown Wig sticks on its victim. */
export const CLOWN_HAT = "/images/clown-wig.svg";

/**
 * Your Round: the victim buys everyone else in their pub a drink, at
 * `perHead` coins each, for at most `maxPeople`, and never more than
 * `maxShare` of their balance.
 */
export const YOUR_ROUND = { perHead: 10, maxPeople: 6, maxShare: 0.25 } as const;

/** Police Raid: how long everyone at the busiest pub gets locked up. */
export const RAID_JAIL_HOURS = 0.25;

/**
 * Cat Burglar: `steal` chance of nicking one of their looks (only ones
 * costing up to `maxItemPrice`; the prestige hats are under lock and key),
 * `caught` chance of being caught and paying them `fine` (as much as you
 * have) plus `jailHours` in jail. Otherwise you get away with nothing.
 */
export const CAT_BURGLAR = {
  steal: 0.4,
  caught: 0.3,
  maxItemPrice: 1000,
  fine: 50,
  jailHours: 0.5,
} as const;

interface TimeLike {
  toDate(): Date;
}

interface MillisLike {
  toMillis(): number;
}

/** Anything a curse can be read off: a spite, or a session's record of one. */
interface SpiteLike {
  options?: { [x: string]: string | number | boolean };
}

const CURSE_KINDS = new Set<string>(Object.keys(CURSE_SPITE));

/** The curse a spite (or its shop item) carries, if any. */
export function getCurseKind(spite: SpiteLike | undefined): CurseKind | null {
  const kind = spite?.options?.curse;

  return typeof kind === "string" && CURSE_KINDS.has(kind) ? (kind as CurseKind) : null;
}

/** `spites` still in force at `now` that carry the curse `kind`. */
export function activeCurses<T extends SpiteLike & { expires: MillisLike }>(
  spites: T[],
  kind: CurseKind,
  now: Date = new Date(),
): T[] {
  return spites.filter(
    (spite) => getCurseKind(spite) === kind && spite.expires.toMillis() > now.getTime(),
  );
}

/**
 * A curse that hit a pub session, as recorded on the session when it ends,
 * so its earnings can be worked out (and recomputed) from the session alone.
 */
export interface SessionCurseLike {
  kind: CurseKind;
  ownerId: string;
  ownerName: string;
  from: TimeLike;
  until: TimeLike;
}

/** The parts of a session its curses need. */
interface CursedSession extends PowerupSession {
  profileId: string;
  startTime: TimeLike;
  endTime: TimeLike | null;
  nightTimeBefore?: number | null;
  nightStart?: TimeLike | null;
}

export interface Siphon {
  profileId: string;
  name: string;
  coin: number;
}

export interface CursedEarnings {
  /** Hourly coin the player keeps, unrounded. */
  hourly: number;
  /** Hourly coin drained by each Leech, rounded down and capped. */
  siphoned: Siphon[];
  /** Hourly coin lost to jail and flat pints, unrounded. */
  lost: number;
}

const ECONOMY_CURSES = new Set<CurseKind>(["jail", "flatPint", "leech"]);

/** Whether a session's curses include one that changes what it earns. */
export function affectsEarnings(curses: SessionCurseLike[]) {
  return curses.some((curse) => ECONOMY_CURSES.has(curse.kind));
}

/** Whether `curses` have the player in jail at `at`. */
export function isJailedAt(curses: SessionCurseLike[], at: Date) {
  const time = at.getTime();

  return curses.some(
    (curse) =>
      curse.kind === "jail" &&
      curse.from.toDate().getTime() <= time &&
      time < curse.until.toDate().getTime(),
  );
}

/**
 * A session's hourly coin once its curses have had their way, by `now` (or
 * by its end, once it's over). Time is split wherever a curse starts or
 * stops: jail earns nothing, a flat pint earns half, and a Leech drains half
 * of whatever's left to whoever cast it (split between Leeches if there's
 * more than one), never more than LEECH.cap each. Curses work on the
 * power-up-boosted rate (see powerups.ts), so a happy hour in jail still
 * earns nothing.
 */
export function getCursedEarnings(
  session: CursedSession,
  curses: SessionCurseLike[] = [],
  now: Date = new Date(),
): CursedEarnings {
  const start = session.startTime.toDate().getTime();
  const end = Math.max(session.endTime?.toDate().getTime() ?? now.getTime(), start);
  const ratePerMs = session.coinRate / 3_600_000;
  const relevant = curses.filter(
    (curse) =>
      ECONOMY_CURSES.has(curse.kind) &&
      curse.from.toDate().getTime() < end &&
      curse.until.toDate().getTime() > start,
  );

  // Power-ups (see powerups.ts) change the rate too, so split where they do.
  const marks = new Set([
    start,
    end,
    ...getPowerupMarks(session).map((mark) => Math.min(Math.max(mark, start), end)),
  ]);
  for (const curse of relevant) {
    marks.add(Math.min(Math.max(curse.from.toDate().getTime(), start), end));
    marks.add(Math.min(Math.max(curse.until.toDate().getTime(), start), end));
  }
  const points = [...marks].sort((a, b) => a - b);

  let hourly = 0;
  let lost = 0;
  const drained = new Map<string, Siphon>();

  for (let index = 0; index < points.length - 1; index++) {
    const from = points[index];
    const to = points[index + 1];
    const middle = (from + to) / 2;
    const live = relevant.filter(
      (curse) => curse.from.toDate().getTime() <= middle && middle < curse.until.toDate().getTime(),
    );
    const earned = (to - from) * ratePerMs * getPowerupMultiplier(session, new Date(middle));

    if (live.some((curse) => curse.kind === "jail")) {
      lost += earned;
      continue;
    }

    const kept = live.some((curse) => curse.kind === "flatPint") ? earned * FLAT_PINT_RATE : earned;
    lost += earned - kept;

    // Leeching yourself (say, one that bounced back) drains nothing.
    const leeches = [
      ...new Map(
        live
          .filter((curse) => curse.kind === "leech" && curse.ownerId !== session.profileId)
          .map((curse) => [curse.ownerId, curse]),
      ).values(),
    ];
    const drainedHere = leeches.length > 0 ? kept * LEECH.share : 0;

    for (const leech of leeches) {
      const siphon = drained.get(leech.ownerId) ?? {
        profileId: leech.ownerId,
        name: leech.ownerName,
        coin: 0,
      };

      siphon.coin += drainedHere / leeches.length;
      drained.set(leech.ownerId, siphon);
    }

    hourly += kept - drainedHere;
  }

  // Anything a capped Leech couldn't take stays with the player.
  const siphoned = [...drained.values()]
    .map((siphon) => {
      const leechCount = relevant.filter(
        (curse) => curse.kind === "leech" && curse.ownerId === siphon.profileId,
      ).length;
      const coin = Math.min(Math.floor(siphon.coin), LEECH.cap * leechCount);

      hourly += siphon.coin - coin;

      return { ...siphon, coin };
    })
    .filter((siphon) => siphon.coin > 0);

  return { hourly, siphoned, lost };
}

/**
 * When a session crosses each night-time milestone (see DURATION_MILESTONES),
 * keyed by bonus id, so bonuses that came up while in jail can be
 * withheld.
 */
export function getMilestoneTimes(session: CursedSession): Map<string, Date> {
  const times = new Map<string, Date>();

  if (!session.nightStart) {
    return times;
  }

  const start = session.startTime.toDate().getTime();
  const before = session.nightTimeBefore ?? 0;

  for (const milestone of DURATION_MILESTONES) {
    const markMs = milestone.hours * 3_600_000;

    if (markMs > before) {
      times.set(`duration-${milestone.hours}h`, new Date(start + markMs - before));
    }
  }

  return times;
}

/** What a spite costs to cast at someone, given the spites already on them. */
export function getKickMePrice<T extends SpiteLike & { expires: MillisLike }>(
  price: number,
  onRecipient: T[],
  now: Date = new Date(),
) {
  return activeCurses(onRecipient, "kickMe", now).length > 0
    ? Math.ceil(price * KICK_ME_DISCOUNT)
    : price;
}

/** What a Loan Shark takes from a gambling win of `winnings`. */
export function getLoanSharkCut(winnings: number) {
  return Math.min(Math.floor(winnings * LOAN_SHARK.share), LOAN_SHARK.cap);
}

/**
 * What a round costs someone with `coin` buying for `people` others: what
 * each gets, and how many of them it covers.
 */
export function getRound(coin: number, people: number) {
  const covered = Math.min(people, YOUR_ROUND.maxPeople);
  const budget = Math.floor(Math.max(coin, 0) * YOUR_ROUND.maxShare);
  const each = Math.min(YOUR_ROUND.perHead, covered > 0 ? Math.floor(budget / covered) : 0);

  return { each, covered };
}

export type BurglaryOutcome = "steal" | "caught" | "nothing";

export function pickBurglaryOutcome(roll: number): BurglaryOutcome {
  if (roll < CAT_BURGLAR.steal) {
    return "steal";
  }

  if (roll < CAT_BURGLAR.steal + CAT_BURGLAR.caught) {
    return "caught";
  }

  return "nothing";
}
